import { useCallback, useEffect, useRef, useState } from 'react';

/** The slice of the Web Speech API this feature uses; the DOM lib has no types for it. */
interface RecognitionResult {
  readonly isFinal: boolean;
  readonly 0: { readonly transcript: string };
}
interface RecognitionEvent {
  readonly resultIndex: number;
  readonly results: ArrayLike<RecognitionResult>;
}
interface Recognition {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((event: RecognitionEvent) => void) | null;
  onerror: ((event: { readonly error: string }) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
}
type RecognitionCtor = new () => Recognition;

/** The browser's own speech recognition, or null where there is none (the mic button is then not rendered). */
export function speechRecognitionCtor(): RecognitionCtor | null {
  const scope = globalThis as unknown as { SpeechRecognition?: RecognitionCtor; webkitSpeechRecognition?: RecognitionCtor };
  return scope.SpeechRecognition ?? scope.webkitSpeechRecognition ?? null;
}

export type VoiceState = 'idle' | 'listening' | 'denied' | 'failed' | 'empty';

const NOTICE_KEY = 'ss.assistant.voiceNoticeSeen';
const readSeen = () => {
  try {
    return globalThis.localStorage?.getItem(NOTICE_KEY) === '1';
  } catch {
    return false;
  }
};
const writeSeen = () => {
  try {
    globalThis.localStorage?.setItem(NOTICE_KEY, '1');
  } catch {
    // Storage is blocked: the notice is simply shown again next time.
  }
};

/**
 * Voice input through the browser's SpeechRecognition only (REQ-110-035): nothing is recorded, uploaded or sent
 * to a service of ours. The transcript goes to `onText`; the member checks it and sends it. The first use shows
 * a notice that the browser's own speech service may process the audio. Listening ends (abort, results ignored)
 * as soon as `active` turns false, because the panel stays mounted while closed.
 */
export function useSpeechInput(lang: string, onText: (text: string) => void, active = true) {
  const supported = speechRecognitionCtor() !== null;
  const [state, setState] = useState<VoiceState>('idle');
  const [noticeVisible, setNoticeVisible] = useState(false);
  const recognition = useRef<Recognition | null>(null);
  const textRef = useRef(onText);
  textRef.current = onText;

  const stop = useCallback(() => {
    const current = recognition.current;
    recognition.current = null;
    if (current) {
      current.onresult = null;
      current.onerror = null;
      current.onend = null;
      current.stop();
    }
    setState((now) => (now === 'listening' ? 'idle' : now));
  }, []);

  const start = useCallback(() => {
    const Ctor = speechRecognitionCtor();
    if (!Ctor) return;
    const fresh = new Ctor();
    fresh.lang = lang;
    fresh.continuous = false;
    fresh.interimResults = false;
    let heard = false;
    let error: VoiceState | null = null;
    fresh.onresult = (event) => {
      let text = '';
      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const result = event.results[index];
        if (result?.isFinal) text += result[0].transcript;
      }
      text = text.trim();
      if (text) {
        heard = true;
        textRef.current(text);
      }
    };
    fresh.onerror = (event) => {
      if (event.error === 'not-allowed' || event.error === 'service-not-allowed') error = 'denied';
      else if (event.error === 'no-speech') error = 'empty';
      else if (event.error !== 'aborted') error = 'failed';
    };
    fresh.onend = () => {
      if (recognition.current === fresh) recognition.current = null;
      setState(error ?? (heard ? 'idle' : 'empty'));
    };
    recognition.current = fresh;
    setNoticeVisible(!readSeen());
    writeSeen();
    setState('listening');
    try {
      fresh.start();
    } catch {
      recognition.current = null;
      setState('failed');
    }
  }, [lang]);

  const toggle = useCallback(() => (recognition.current ? stop() : start()), [start, stop]);
  useEffect(() => {
    if (active) return undefined;
    const current = recognition.current;
    if (!current) return undefined;
    recognition.current = null;
    current.onresult = null;
    current.onerror = null;
    current.onend = null;
    current.abort();
    setState('idle');
    return undefined;
  }, [active]);
  useEffect(
    () => () => {
      const current = recognition.current;
      if (current) {
        current.onresult = null;
        current.abort();
      }
    },
    [],
  );

  return { supported, state, noticeVisible, toggle };
}
